// src/services/requestsService.js
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  addDoc, 
  setDoc,
  updateDoc, 
  deleteDoc,
  writeBatch,
  query,
  where,
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage, functions } from '../config/firebase';

const normalizeOfferStatus = (status) => {
  const raw = String(status || '').trim().toLowerCase();
  if (raw === 'accepted') return 'accepted';
  if (raw === 'declined' || raw === 'rejected') return 'declined';
  if (raw === 'pending') return 'pending';
  if (raw === 'cancelled' || raw === 'canceled') return 'cancelled';
  return raw || 'pending';
};

const normalizeMissionStage = (value, fallback) => {
  const stage = String(value || '').trim().toLowerCase().replace(/\s+/g, '_');
  return stage || fallback;
};

export const requestsService = {
  
  // ==================== PROVIDER PROFILE METHODS ====================
  
  async createProviderProfile(bookId, userId, profileData) {
    try {
      const providerRef = doc(db, 'pb-books', bookId, 'pb-providers', userId);
      const providerStatus = profileData.status || (profileData.verified ? 'active' : 'pending_approval');
      const providerData = {
        providerId: userId,
        providerUserId: userId,
        bookId: bookId,
        businessName: profileData.businessName,
        categories: profileData.categories || [],
        description: profileData.description || '',
        email: profileData.email || '',
        phone: profileData.phone || '',
        website: profileData.website || '',
        logo: profileData.logo || '',
        verified: profileData.verified || false,
        featured: profileData.featured || false,
        status: providerStatus,
        agentStatus: providerStatus,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...Object.keys(profileData)
          .filter(key => !['businessName', 'categories', 'description', 'email', 'phone', 'website', 'logo', 'verified', 'featured', 'status'].includes(key))
          .reduce((obj, key) => {
            obj[key] = profileData[key];
            return obj;
          }, {})
      };

      await setDoc(providerRef, providerData, { merge: true });
      return userId;
    } catch (error) {
      console.error('Error creating provider profile:', error);
      throw error;
    }
  },

  async getProviderProfiles(bookId) {
    try {
      const providersRef = collection(db, 'pb-books', bookId, 'pb-providers');
      const q = query(providersRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const providers = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      
      return providers.sort((a, b) => {
        if (a.featured && !b.featured) return -1;
        if (!a.featured && b.featured) return 1;
        if (a.verified && !b.verified) return -1;
        if (!a.verified && b.verified) return 1;
        return 0;
      });
    } catch (error) {
      console.error('Error getting provider profiles:', error);
      return [];
    }
  },

  async getProviderProfile(bookId, providerId) {
    try {
      const providerRef = doc(db, 'pb-books', bookId, 'pb-providers', providerId);
      const docSnap = await getDoc(providerRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting provider profile:', error);
      return null;
    }
  },

  async getProviderProfileByUserId(bookId, userId) {
    try {
      const directProfile = await this.getProviderProfile(bookId, userId);
      if (directProfile) {
        return directProfile;
      }

      const providersRef = collection(db, 'pb-books', bookId, 'pb-providers');

      for (const fieldName of ['providerUserId', 'providerId']) {
        const q = query(providersRef, where(fieldName, '==', userId));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const docSnap = snapshot.docs[0];
          return { id: docSnap.id, ...docSnap.data() };
        }
      }

      return null;
    } catch (error) {
      console.error('Error getting provider profile by user ID:', error);
      return null;
    }
  },

  async migrateProviderProfileToCanonicalId(bookId, userId, existingProfile) {
    try {
      if (!existingProfile) return null;

      const canonicalRef = doc(db, 'pb-books', bookId, 'pb-providers', userId);
      const canonicalData = {
        ...existingProfile,
        providerId: userId,
        providerUserId: userId,
        bookId,
        updatedAt: serverTimestamp()
      };

      delete canonicalData.id;

      await setDoc(canonicalRef, canonicalData, { merge: true });
      return { id: userId, ...existingProfile, providerId: userId, providerUserId: userId, bookId };
    } catch (error) {
      console.error('Error migrating provider profile:', error);
      throw error;
    }
  },

  async updateProviderProfile(bookId, providerId, updates) {
    try {
      const providerRef = doc(db, 'pb-books', bookId, 'pb-providers', providerId);
      await updateDoc(providerRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating provider profile:', error);
      throw error;
    }
  },

  async deleteProviderProfile(bookId, providerId) {
    try {
      const providerRef = doc(db, 'pb-books', bookId, 'pb-providers', providerId);
      await deleteDoc(providerRef);
    } catch (error) {
      console.error('Error deleting provider profile:', error);
      throw error;
    }
  },

  async getProviderCredits(bookId, providerId) {
    try {
      const creditRef = doc(db, 'pb-books', bookId, 'pb-provider-credits', providerId);
      const creditSnap = await getDoc(creditRef);

      if (!creditSnap.exists()) {
        return null;
      }

      return {
        id: creditSnap.id,
        ...creditSnap.data()
      };
    } catch (error) {
      console.error('Error getting provider credits:', error);
      return null;
    }
  },

  async initializeProviderCredits(bookId, providerId, creditData) {
    try {
      const creditRef = doc(db, 'pb-books', bookId, 'pb-provider-credits', providerId);
      const payload = {
        providerId,
        bookId,
        credits: creditData.credits ?? 0,
        dailyLimit: creditData.dailyLimit ?? 0,
        weeklyLimit: creditData.weeklyLimit ?? 0,
        spentToday: creditData.spentToday ?? 0,
        spentWeek: creditData.spentWeek ?? 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await setDoc(creditRef, payload, { merge: true });
      return payload;
    } catch (error) {
      console.error('Error initializing provider credits:', error);
      throw error;
    }
  },

  async updateProviderCredits(bookId, providerId, updates) {
    try {
      const creditRef = doc(db, 'pb-books', bookId, 'pb-provider-credits', providerId);
      await setDoc(creditRef, {
        providerId,
        bookId,
        ...updates,
        updatedAt: serverTimestamp()
      }, { merge: true });
    } catch (error) {
      console.error('Error updating provider credits:', error);
      throw error;
    }
  },

  async createProviderAgent(bookId, providerId, agentData) {
    try {
      const agentsRef = collection(db, 'pb-books', bookId, 'pb-provider-agents');
      const payload = {
        providerId,
        bookId,
        name: agentData.name,
        description: agentData.description || '',
        categories: agentData.categories || [],
        autoEngage: agentData.autoEngage || false,
        status: agentData.status || 'active',
        dailyLimit: agentData.dailyLimit ?? 50,
        weeklyLimit: agentData.weeklyLimit ?? 200,
        spentToday: agentData.spentToday ?? 0,
        spentWeek: agentData.spentWeek ?? 0,
        isDefault: agentData.isDefault || false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...Object.keys(agentData)
          .filter(key => !['name', 'description', 'categories', 'autoEngage', 'status', 'dailyLimit', 'weeklyLimit', 'spentToday', 'spentWeek', 'isDefault'].includes(key))
          .reduce((obj, key) => {
            obj[key] = agentData[key];
            return obj;
          }, {})
      };

      const docRef = await addDoc(agentsRef, payload);
      return docRef.id;
    } catch (error) {
      console.error('Error creating provider agent:', error);
      throw error;
    }
  },

  async getProviderAgents(bookId, providerId) {
    try {
      const agentsRef = collection(db, 'pb-books', bookId, 'pb-provider-agents');
      const q = query(
        agentsRef,
        where('providerId', '==', providerId),
        orderBy('createdAt', 'asc')
      );
      const snapshot = await getDocs(q);

      return snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
    } catch (error) {
      console.error('Error getting provider agents:', error);
      return [];
    }
  },

  async updateProviderAgent(bookId, agentId, updates) {
    try {
      const agentRef = doc(db, 'pb-books', bookId, 'pb-provider-agents', agentId);
      await updateDoc(agentRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating provider agent:', error);
      throw error;
    }
  },

  async deleteProviderAgent(bookId, agentId) {
    try {
      const agentRef = doc(db, 'pb-books', bookId, 'pb-provider-agents', agentId);
      await deleteDoc(agentRef);
    } catch (error) {
      console.error('Error deleting provider agent:', error);
      throw error;
    }
  },

  async getProvidersByCategory(bookId, category) {
    try {
      const providersRef = collection(db, 'pb-books', bookId, 'pb-providers');
      const q = query(
        providersRef, 
        where('categories', 'array-contains', category),
        where('status', '==', 'active')
      );
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
    } catch (error) {
      console.error('Error getting providers by category:', error);
      return [];
    }
  },

  async getVerifiedProviders(bookId) {
    try {
      const providersRef = collection(db, 'pb-books', bookId, 'pb-providers');
      const q = query(
        providersRef, 
        where('verified', '==', true),
        where('status', '==', 'active')
      );
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
    } catch (error) {
      console.error('Error getting verified providers:', error);
      return [];
    }
  },

  async getFeaturedProviders(bookId) {
    try {
      const providersRef = collection(db, 'pb-books', bookId, 'pb-providers');
      const q = query(
        providersRef, 
        where('featured', '==', true),
        where('status', '==', 'active')
      );
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
    } catch (error) {
      console.error('Error getting featured providers:', error);
      return [];
    }
  },

  // ==================== REQUEST METHODS ====================

  async createRequest(bookId, userId, requestData) {
    try {
      if (!userId) {
        throw new Error('Authentication is required to create a mission');
      }

      const requestsRef = collection(db, 'pb-books', bookId, 'pb-requests');
      const demandProfile = requestData.demandProfile || {};
      
      const request = {
        bookId: bookId,
        userId,
        title: requestData.title || demandProfile.summary || 'Untitled Mission',
        description: requestData.description,
        category: requestData.category || 'general',
        vertical: requestData.vertical || 'professional_services',
        budget: requestData.budget || requestData.budgetMax || null,
        budgetMin: requestData.budgetMin || null,
        budgetMax: requestData.budgetMax || null,
        timeframe: requestData.timeframe || '',
        location: requestData.location || '',
        status: 'open',
        type: 'agent_king_mission',
        workflowStage: 'submitted',
        executionStage: 'awaiting_offer_approval',
        nextActions: [
          'Review ranked shortlist',
          'Approve an offer for execution',
          'Approve checkout',
          'Confirm external payment'
        ],
        demandProfile,
        verticalData: requestData.verticalData || {},
        externalDiscovery: {
          status: 'idle',
          externalLeadCount: 0,
          internalMatchCount: 0,
          existingOfferCount: 0
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...requestData
      };

      const docRef = await addDoc(requestsRef, request);
      return docRef.id;
    } catch (error) {
      console.error('Error creating request:', error);
      throw error;
    }
  },

  async getOpenRequests(bookId, category = null) {
    try {
      const requestsRef = collection(db, 'pb-books', bookId, 'pb-requests');
      let q;
      
      if (category) {
        q = query(
          requestsRef, 
          where('status', '==', 'open'),
          where('category', '==', category),
          orderBy('createdAt', 'desc')
        );
      } else {
        q = query(
          requestsRef, 
          where('status', '==', 'open'),
          orderBy('createdAt', 'desc')
        );
      }
      
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
    } catch (error) {
      console.error('Error getting open requests:', error);
      return [];
    }
  },

  async getUserRequests(bookId, userId) {
    try {
      if (!userId) return [];

      const requestsRef = collection(db, 'pb-books', bookId, 'pb-requests');
      const q = query(
        requestsRef, 
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);

      return snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
    } catch (error) {
      console.error('Error getting user requests:', error);
      return [];
    }
  },

  async updateRequestStatus(bookId, requestId, status) {
    try {
      const requestRef = doc(db, 'pb-books', bookId, 'pb-requests', requestId);
      await updateDoc(requestRef, {
        status: status,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating request status:', error);
      throw error;
    }
  },

  async updateRequest(bookId, requestId, updates) {
    try {
      const requestRef = doc(db, 'pb-books', bookId, 'pb-requests', requestId);
      await updateDoc(requestRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating request:', error);
      throw error;
    }
  },

  // ==================== OFFER METHODS ====================

  async createOffer(bookId, requestId, providerId, offerData) {
    try {
      const offersRef = collection(db, 'pb-books', bookId, 'pb-offers');
      
      const offer = {
        bookId: bookId,
        requestId: requestId,
        providerId: providerId,
        agentId: offerData.agentId || null,
        agentName: offerData.agentName || null,
        providerName: offerData.providerName,
        providerCategories: offerData.providerCategories || [],
        valueProp: offerData.valueProp,
        price: offerData.price,
        timeframe: offerData.timeframe,
        status: offerData.status || 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...offerData
      };

      const docRef = await addDoc(offersRef, offer);
      return docRef.id;
    } catch (error) {
      console.error('Error creating offer:', error);
      throw error;
    }
  },

  async getOffersForRequest(bookId, requestId) {
    try {
      const offersRef = collection(db, 'pb-books', bookId, 'pb-offers');
      const q = query(
        offersRef, 
        where('requestId', '==', requestId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);

      return snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
    } catch (error) {
      console.error('Error getting offers for request:', error);
      return [];
    }
  },

  async getOffersByProvider(bookId, providerId) {
    try {
      const offersRef = collection(db, 'pb-books', bookId, 'pb-offers');
      const q = query(
        offersRef, 
        where('providerId', '==', providerId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);

      return snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
    } catch (error) {
      console.error('Error getting offers by provider:', error);
      return [];
    }
  },

  async getAllOffers(bookId) {
    try {
      const offersRef = collection(db, 'pb-books', bookId, 'pb-offers');
      const q = query(offersRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      return snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
    } catch (error) {
      console.error('Error getting all offers:', error);
      return [];
    }
  },

  async updateOfferStatus(bookId, offerId, status) {
    try {
      const offerRef = doc(db, 'pb-books', bookId, 'pb-offers', offerId);
      const normalizedStatus = normalizeOfferStatus(status);
      const statusTimestampField = normalizedStatus === 'accepted'
        ? { acceptedAt: serverTimestamp() }
        : normalizedStatus === 'declined'
          ? { declinedAt: serverTimestamp() }
          : {};

      await updateDoc(offerRef, {
        status: normalizedStatus,
        ...statusTimestampField,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating offer status:', error);
      throw error;
    }
  },

  async acceptOffer(bookId, requestId, offerId) {
    try {
      const requestRef = doc(db, 'pb-books', bookId, 'pb-requests', requestId);
      const acceptedOfferRef = doc(db, 'pb-books', bookId, 'pb-offers', offerId);
      const acceptedOfferSnap = await getDoc(acceptedOfferRef);

      if (!acceptedOfferSnap.exists()) {
        throw new Error('Offer not found');
      }

      const acceptedOffer = { id: acceptedOfferSnap.id, ...acceptedOfferSnap.data() };
      const allOffersSnap = await getDocs(query(
        collection(db, 'pb-books', bookId, 'pb-offers'),
        where('requestId', '==', requestId)
      ));

      const batch = writeBatch(db);
      allOffersSnap.docs.forEach((offerDoc) => {
        const nextStatus = offerDoc.id === offerId ? 'accepted' : 'declined';
        const statusMeta = offerDoc.id === offerId
          ? { acceptedAt: serverTimestamp() }
          : { declinedAt: serverTimestamp() };

        batch.update(offerDoc.ref, {
          status: nextStatus,
          ...statusMeta,
          updatedAt: serverTimestamp()
        });
      });

      batch.update(requestRef, {
        status: 'IN_PROGRESS',
        workflowStage: 'in_progress',
        executionStage: 'offer_accepted',
        acceptedOffer: {
          offerId: acceptedOffer.id,
          providerId: acceptedOffer.providerId || null,
          providerName: acceptedOffer.providerName || 'Provider',
          providerType: acceptedOffer.providerType || 'Service Provider',
          valueProp: acceptedOffer.valueProp || '',
          price: acceptedOffer.price || acceptedOffer.priceEstimate || '',
          timeframe: acceptedOffer.timeframe || '',
          acceptedAt: new Date().toISOString()
        },
        updatedAt: serverTimestamp()
      });

      await batch.commit();
      return { success: true, acceptedOfferId: offerId };
    } catch (error) {
      console.error('Error accepting offer:', error);
      throw error;
    }
  },

  async sendAgentOffer(bookId, requestId, agentId, offerDraft = {}, presetId = null) {
    try {
      const callable = httpsCallable(functions, 'sendAgentOffer');
      const result = await callable({
        bookId,
        requestId,
        agentId,
        presetId,
        offerDraft
      });
      return result.data;
    } catch (error) {
      console.error('Error sending agent offer:', error);
      throw error;
    }
  },

  async discoverExternalProvidersForRequest(bookId, requestId, force = false) {
    try {
      const callable = httpsCallable(functions, 'discoverExternalProvidersForRequest');
      const result = await callable({
        bookId,
        requestId,
        force
      });
      return result.data;
    } catch (error) {
      console.error('Error discovering external providers:', error);
      throw error;
    }
  },

  async dispatchExternalRequestOutreach(bookId, requestId, leadIds = [], maxDispatch = 5) {
    try {
      const callable = httpsCallable(functions, 'dispatchExternalRequestOutreach');
      const result = await callable({
        bookId,
        requestId,
        leadIds,
        maxDispatch
      });
      return result.data;
    } catch (error) {
      console.error('Error dispatching external outreach:', error);
      throw error;
    }
  },

  async getExternalProviderLeads(bookId, requestId) {
    try {
      const leadsRef = collection(db, 'pb-books', bookId, 'pb-requests', requestId, 'pb-external-provider-leads');
      const snapshot = await getDocs(leadsRef);

      return snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
    } catch (error) {
      console.error('Error getting external provider leads:', error);
      return [];
    }
  },

  // ==================== PRESET OFFERS METHODS ====================

  async createPresetOffer(bookId, providerId, presetData) {
    try {
      const presetsRef = collection(db, 'pb-books', bookId, 'pb-preset-offers');
      
      const preset = {
        providerId: providerId,
        agentId: presetData.agentId || null,
        bookId: bookId,
        name: presetData.name,
        category: presetData.category,
        valueProp: presetData.valueProp,
        price: presetData.price,
        timeframe: presetData.timeframe,
        description: presetData.description || '',
        autoRespond: presetData.autoRespond || false,
        active: presetData.active !== false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(presetsRef, preset);
      return docRef.id;
    } catch (error) {
      console.error('Error creating preset offer:', error);
      throw error;
    }
  },

  async getProviderPresets(bookId, providerId) {
    try {
      const presetsRef = collection(db, 'pb-books', bookId, 'pb-preset-offers');
      const q = query(
        presetsRef, 
        where('providerId', '==', providerId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);

      return snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
    } catch (error) {
      console.error('Error getting provider presets:', error);
      return [];
    }
  },

  async getAgentPresets(bookId, providerId, agentId) {
    try {
      const presets = await this.getProviderPresets(bookId, providerId);
      return presets.filter(preset => preset.agentId === agentId);
    } catch (error) {
      console.error('Error getting agent presets:', error);
      return [];
    }
  },

  async getAutoRespondPresets(bookId, category) {
    try {
      const presetsRef = collection(db, 'pb-books', bookId, 'pb-preset-offers');
      const q = query(
        presetsRef, 
        where('category', '==', category),
        where('autoRespond', '==', true),
        where('active', '==', true)
      );
      const snapshot = await getDocs(q);

      return snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
    } catch (error) {
      console.error('Error getting auto-respond presets:', error);
      return [];
    }
  },

  async updatePresetOffer(bookId, presetId, updates) {
    try {
      const presetRef = doc(db, 'pb-books', bookId, 'pb-preset-offers', presetId);
      await updateDoc(presetRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating preset offer:', error);
      throw error;
    }
  },

  async deletePresetOffer(bookId, presetId) {
    try {
      const presetRef = doc(db, 'pb-books', bookId, 'pb-preset-offers', presetId);
      await deleteDoc(presetRef);
    } catch (error) {
      console.error('Error deleting preset offer:', error);
      throw error;
    }
  },

  async usePresetOffer(bookId, requestId, providerId, presetId) {
    try {
      const presetRef = doc(db, 'pb-books', bookId, 'pb-preset-offers', presetId);
      const presetSnap = await getDoc(presetRef);

      if (!presetSnap.exists()) {
        throw new Error('Preset not found');
      }

      const preset = { id: presetSnap.id, ...presetSnap.data() };

      const provider = await this.getProviderProfile(bookId, providerId);

      return await this.createOffer(bookId, requestId, providerId, {
        agentId: preset.agentId || null,
        agentName: preset.agentName || preset.name,
        providerName: provider?.businessName || 'Provider',
        providerCategories: provider?.categories || [],
        valueProp: preset.valueProp,
        price: preset.price,
        timeframe: preset.timeframe,
        status: 'pending',
        presetId: presetId,
        presetName: preset.name
      });
    } catch (error) {
      console.error('Error using preset offer:', error);
      throw error;
    }
  },

  // ==================== STATISTICS ====================

  async getStats(bookId) {
    try {
      const [requestsSnap, offersSnap, providersSnap] = await Promise.all([
        getDocs(collection(db, 'pb-books', bookId, 'pb-requests')),
        getDocs(collection(db, 'pb-books', bookId, 'pb-offers')),
        getDocs(collection(db, 'pb-books', bookId, 'pb-providers'))
      ]);

      const requests = requestsSnap.docs.map(d => d.data());
      const offers = offersSnap.docs.map(d => d.data());
      const providers = providersSnap.docs.map(d => d.data());

      return {
        totalRequests: requests.length,
        openRequests: requests.filter(r => r.status === 'open').length,
        totalOffers: offers.length,
        pendingOffers: offers.filter(o => o.status === 'pending').length,
        totalProviders: providers.length,
        verifiedProviders: providers.filter(p => p.verified).length,
        featuredProviders: providers.filter(p => p.featured).length
      };
    } catch (error) {
      console.error('Error getting stats:', error);
      return {
        totalRequests: 0,
        openRequests: 0,
        totalOffers: 0,
        pendingOffers: 0,
        totalProviders: 0,
        verifiedProviders: 0,
        featuredProviders: 0
      };
    }
  },

  async generateAIOffers(bookId, requestId, requestData) {
    try {
      if (!bookId || !requestId) return [];
      const mission = await this.launchMission(bookId, requestId);
      return mission.shortlist?.length ? mission.shortlist : mission.offers || [];
    } catch (error) {
      console.error('Error generating AI offers:', error);
      return [];
    }
  },

  async buildMissionShortlist(bookId, requestId) {
    try {
      const callable = httpsCallable(functions, 'buildMissionShortlist');
      const result = await callable({ bookId, requestId });
      return result.data;
    } catch (error) {
      console.error('Error building mission shortlist:', error);
      throw error;
    }
  },

  async launchMission(bookId, requestId) {
    try {
      const shortlistResult = await this.buildMissionShortlist(bookId, requestId);

      if (
        (!shortlistResult?.shortlist || shortlistResult.shortlist.length === 0) &&
        Number(shortlistResult?.internalCoverage?.eligibleAgentCount || 0) === 0
      ) {
        try {
          const discovery = await this.discoverExternalProvidersForRequest(bookId, requestId, false);
          shortlistResult.discovery = discovery?.discovery || shortlistResult.discovery || null;
          shortlistResult.externalLeads = discovery?.leads || [];
        } catch (discoveryError) {
          console.error('Mission launch external discovery failed:', discoveryError);
        }
      }

      return shortlistResult;
    } catch (error) {
      console.error('Error launching mission:', error);
      throw error;
    }
  },

  async refreshMissionShortlist(bookId, requestId) {
    return this.launchMission(bookId, requestId);
  },

  async approveMissionOffer(bookId, requestId, offerId) {
    try {
      const callable = httpsCallable(functions, 'approveMissionOffer');
      const result = await callable({ bookId, requestId, offerId });
      return result.data;
    } catch (error) {
      console.error('Error approving mission offer:', error);
      throw error;
    }
  },

  async getRequestExecutions(bookId, requestId) {
    try {
      const executionsRef = collection(db, 'pb-books', bookId, 'pb-requests', requestId, 'pb-executions');
      const q = query(executionsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
    } catch (error) {
      console.error('Error getting request executions:', error);
      return [];
    }
  },

  async prepareMissionExecution(bookId, requestId, executionId, executionOverrides = {}) {
    try {
      const callable = httpsCallable(functions, 'prepareMissionExecution');
      const result = await callable({ bookId, requestId, executionId, executionOverrides });
      return result.data;
    } catch (error) {
      console.error('Error preparing mission execution:', error);
      throw error;
    }
  },

  async approveMissionExecution(bookId, requestId, executionId) {
    try {
      const callable = httpsCallable(functions, 'approveMissionExecution');
      const result = await callable({ bookId, requestId, executionId });
      return result.data;
    } catch (error) {
      console.error('Error approving mission execution:', error);
      throw error;
    }
  },

  async generateMissionCheckout(bookId, requestId, executionId) {
    try {
      const callable = httpsCallable(functions, 'generateMissionCheckout');
      const result = await callable({ bookId, requestId, executionId });
      return result.data;
    } catch (error) {
      console.error('Error generating mission checkout:', error);
      throw error;
    }
  },

  async confirmExternalPayment(bookId, requestId, executionId, paymentReference, notes = '') {
    try {
      const callable = httpsCallable(functions, 'confirmExternalPayment');
      const result = await callable({ bookId, requestId, executionId, paymentReference, notes });
      return result.data;
    } catch (error) {
      console.error('Error confirming external payment:', error);
      throw error;
    }
  },

  async advanceMissionExecution(bookId, requestId, executionId, action, payload = {}) {
    try {
      const callable = httpsCallable(functions, 'advanceMissionExecution');
      const result = await callable({ bookId, requestId, executionId, action, payload });
      return result.data;
    } catch (error) {
      console.error('Error advancing mission execution:', error);
      throw error;
    }
  }
};
